import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFiscalYear } from '../context/FiscalYearContext';
import { useAcademicYear } from '../context/AcademicYearContext';
import { useT } from '../i18n/LanguageContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useKeyboardShortcuts, useSequenceShortcut } from '../hooks/useKeyboardShortcuts';
import ShortcutsHelpDialog from './ShortcutsHelpDialog';
import AppFooter from './AppFooter';
import { getVersionString } from '../config/version';
import {
  LayoutDashboard,
  BookOpen,
  Sparkles,
  Receipt,
  DollarSign,
  FileBarChart,
  TrendingUp,
  LogOut,
  ChevronDown,
  Building2,
  User,
  Menu,
  X,
  Settings,
  FileText,
  Users,
  Truck,
  Package,
  ShoppingCart,
  PanelLeftClose,
  PanelLeft,
  Calendar,
  Lock,
  Unlock,
  RotateCcw,
  Monitor,
  ClipboardList,
  AlertTriangle,
  ScanLine,
  GraduationCap,
  School,
  Pencil,
  Award,
  SlidersHorizontal,
  CalendarCheck,
  CalendarRange,
  HeartHandshake,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Button } from '../components/ui/button';
import { getRoleDisplayName } from '../lib/utils';

const ADMIN_ROLES = ['super_admin', 'admin'];

const Sidebar = ({ isOpen, onClose, collapsed, onToggleCollapse }) => {
  const location = useLocation();
  const { user, canEdit, canAdmin, isSuperAdmin, currentOrg } = useAuth();
  const { t } = useT();
  const role = user?.role;

  // ---- School module nav (admin/super_admin) ----
  const schoolItems = [
    { path: '/school', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/school/families', icon: HeartHandshake, label: t('nav.families', 'Family/Guardian') },
    { path: '/school/students', icon: GraduationCap, label: t('nav.students') },
    { path: '/school/teachers', icon: Users, label: t('nav.teachers') },
    { path: '/school/classes', icon: School, label: t('nav.classes') },
    { path: '/school/subjects', icon: BookOpen, label: t('nav.subjects') },
    { path: '/school/enrollment', icon: ClipboardList, label: t('nav.enrollment') },
    { path: '/school/exams', icon: FileText, label: t('nav.exams') },
    { path: '/school/marks', icon: Pencil, label: t('nav.marks') },
    { path: '/school/attendance', icon: CalendarCheck, label: t('nav.attendance') },
    { path: '/school/report-cards', icon: Award, label: t('nav.reportCards') },
    { path: '/school/timetable', icon: CalendarRange, label: t('nav.timetable') },
    { path: '/school/grading-scales', icon: SlidersHorizontal, label: t('nav.gradingScales') },
    { path: '/school/academic-years', icon: Calendar, label: t('nav.academicYears') },
    { path: '/school/oustaz', icon: Sparkles, label: 'OUSTAZ.AI' },
    { path: '/school/fees/groups', icon: DollarSign, label: t('nav.feesGroups', 'Fees Groups') },
    { path: '/school/fees/types', icon: SlidersHorizontal, label: t('nav.feesTypes', 'Fees Types') },
    { path: '/school/fees/invoices/new', icon: Receipt, label: t('nav.newInvoice', 'New Fees Invoice') },
    { path: '/school/fees/invoices', icon: FileText, label: t('nav.feesInvoices', 'Fees Invoices') },
    { path: '/school/fees/dashboard', icon: FileBarChart, label: t('nav.feesDashboard', 'Fees Dashboard') },
    { path: '/school/fees/statements', icon: Receipt, label: t('nav.guardianStatements', 'Guardian Statements') },
    { path: '/school/fees/overdue', icon: AlertTriangle, label: t('nav.overdueReminders', 'Overdue / Reminders') },
    { path: '/school/fees/journal', icon: ClipboardList, label: t('nav.feesJournal', 'Accounting Journal') },
    { path: '/school/fees/settings', icon: SlidersHorizontal, label: t('nav.financeSettings', 'Finance Settings') },
  ];

  // ---- Finance module nav (accounting base) ----
  const financeItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/chart-of-accounts', icon: BookOpen, label: 'Chart of Accounts' },
    { path: '/vouchers', icon: Receipt, label: 'Voucher Entry', requiresEdit: true },
    { path: '/cr-db-notes', icon: FileText, label: 'Cr/Db Notes', requiresEdit: true },
    { path: '/sales-quotations', icon: FileText, label: 'Sales Quotations', requiresEdit: true },
    { path: '/sales-invoices', icon: ShoppingCart, label: 'Sales Invoices', requiresEdit: true },
    { path: '/sales-returns', icon: RotateCcw, label: 'Sales Returns', requiresEdit: true },
    { path: '/purchase-invoices', icon: Truck, label: 'Purchase Invoices', requiresEdit: true },
    { path: '/purchase-orders', icon: ClipboardList, label: 'Purchase Orders', requiresEdit: true },
    { path: '/purchase-returns', icon: RotateCcw, label: 'Purchase Returns', requiresEdit: true },
    { path: '/suppliers', icon: Truck, label: 'Suppliers' },
    { path: '/inventory', icon: Package, label: 'Inventory' },
    { path: '/pos', icon: Monitor, label: 'POS Terminal', requiresEdit: true },
    { path: '/exchange-rates', icon: DollarSign, label: 'Exchange Rates' },
    { path: '/trial-balance', icon: FileBarChart, label: 'Trial Balance' },
    { path: '/journal', icon: BookOpen, label: 'Journal' },
    { path: '/income-statement', icon: TrendingUp, label: 'Income Statement' },
  ];

  // Filter a list of nav items against the role's saved permissions.
  // A module is shown only when the role has it toggled on (object format:
  // { path: [actions] } with at least one action). Falls back to legacy array
  // format and finally to the item's own requiresX gates when no perms are saved.
  const filterByPermissions = (items) => items.filter((item) => {
    if (isSuperAdmin()) return true;
    const rolePerms = currentOrg?.role_permissions;
    if (rolePerms && role && rolePerms[role]) {
      const userRolePerms = rolePerms[role];
      if (typeof userRolePerms === 'object' && !Array.isArray(userRolePerms)) {
        const modulePerms = userRolePerms[item.path];
        return Array.isArray(modulePerms) && modulePerms.length > 0;
      }
      if (Array.isArray(userRolePerms)) return userRolePerms.includes(item.path);
    }
    if (item.requiresSuperAdmin) return false;
    if (item.requiresAdmin && !canAdmin()) return false;
    if (item.requiresEdit && !canEdit()) return false;
    return true;
  });
  const filterFinance = filterByPermissions; // back-compat alias

  // Build groups based on role
  const groups = [];
  if (ADMIN_ROLES.includes(role)) {
    groups.push({ key: 'school', label: t('nav.school'), items: schoolItems });
    groups.push({ key: 'finance', label: t('nav.finance'), items: filterByPermissions(financeItems) });
  } else if (role === 'accountant' || role === 'cashier' || role === 'viewer') {
    // These roles can be granted School/Fees modules via Role Permissions too, so
    // honour those toggles — not just the base Finance modules.
    const schoolAllowed = filterByPermissions(schoolItems);
    if (schoolAllowed.length) groups.push({ key: 'school', label: t('nav.school'), items: schoolAllowed });
    const financeAllowed = filterByPermissions(financeItems);
    if (financeAllowed.length) groups.push({ key: 'finance', label: t('nav.finance'), items: financeAllowed });
  } else if (role === 'teacher') {
    groups.push({ key: 'portal', label: t('nav.portal'), items: [
      { path: '/portal/teacher', icon: School, label: t('portal.teacherTitle') },
      { path: '/portal/teacher/timetable', icon: CalendarRange, label: t('portal.myTimetable', 'My Timetable') },
      { path: '/school/attendance', icon: CalendarCheck, label: t('nav.attendance') },
      { path: '/school/marks', icon: Pencil, label: t('nav.marks') },
      { path: '/school/oustaz', icon: Sparkles, label: 'OUSTAZ.AI' },
    ] });
  } else if (role === 'student') {
    groups.push({ key: 'portal', label: t('nav.portal'), items: [
      { path: '/portal/student', icon: GraduationCap, label: t('portal.studentTitle') },
      { path: '/portal/student/timetable', icon: CalendarRange, label: t('portal.myTimetable', 'My Timetable') },
    ] });
  } else if (role === 'parent') {
    groups.push({ key: 'portal', label: t('nav.portal'), items: [{ path: '/portal/parent', icon: Users, label: t('portal.parentTitle') }] });
  }

  // Administration group (super_admin only) - multi-organization control
  if (role === 'super_admin') {
    groups.push({ key: 'admin', label: t('nav.administration'), items: [
      { path: '/settings', icon: Settings, label: t('nav.settings') },
    ] });
  }

  const renderItem = (item) => {
    const isActive = location.pathname === item.path;
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={onClose}
        title={collapsed ? item.label : undefined}
        className={`flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${collapsed ? 'justify-center' : ''} ${
          isActive
            ? 'bg-white/15 text-white border-l-2 border-blue-300 -ml-0.5 pl-3.5 font-medium'
            : 'text-blue-200/70 hover:text-white hover:bg-white/10'
        }`}
        data-testid={`nav-${item.path.replace(/\//g, '-').replace(/^-/, '')}`}
      >
        <item.icon className="w-4 h-4 flex-shrink-0" />
        {!collapsed && <span>{item.label}</span>}
      </Link>
    );
  };

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />}
      <aside
        className={`fixed left-0 top-0 h-full bg-[#1a2744] text-white border-r border-[#1e3a5f] z-50 transform transition-all duration-300 ease-in-out lg:translate-x-0 flex flex-col ${collapsed ? 'w-16' : 'w-64'} ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="sidebar"
      >
        <div className="p-4 flex-shrink-0">
          <div className="flex items-center justify-between lg:hidden mb-4">
            <div className="flex items-center gap-2">
              <img src="/eli-crest.png" alt="E.L.I" className="w-8 h-8 object-contain bg-white rounded p-0.5" />
              {!collapsed && <span className="font-bold text-sm">E.L.I</span>}
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}><X className="w-5 h-5" /></Button>
          </div>

          <div className={`hidden lg:flex items-center mb-6 ${collapsed ? 'justify-center' : 'gap-3 px-1'}`}>
            <img src="/eli-crest.png" alt="E.L.I" className="w-10 h-10 object-contain bg-white rounded p-0.5 flex-shrink-0" />
            {!collapsed && (
              <div className="min-w-0">
                <h1 className="font-bold text-sm text-white leading-tight truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>Escola Libanesa</h1>
                <p className="text-xs text-blue-300/70">{t('app.tagline')}</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 pb-4 scrollbar-thin">
          <nav className="space-y-3">
            {groups.map((group) => (
              <Collapsible key={group.key} defaultOpen className="space-y-1">
                {!collapsed && (
                  <CollapsibleTrigger
                    className="flex items-center justify-between w-full px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-blue-300/60 hover:text-blue-200 transition-colors"
                    data-testid={`sidebar-${group.key}-group-toggle`}
                  >
                    <span>{group.label}</span>
                    <ChevronDown className="w-3.5 h-3.5" />
                  </CollapsibleTrigger>
                )}
                <CollapsibleContent className="space-y-1">
                  {group.items.map(renderItem)}
                </CollapsibleContent>
              </Collapsible>
            ))}
          </nav>
        </div>

        <div className="hidden lg:flex flex-col p-3 border-t border-white/10 flex-shrink-0">
          {!collapsed && <div className="text-[10px] text-blue-300/40 text-center mb-2">Powered by KAIROS &middot; {getVersionString('short')}</div>}
          <Button variant="ghost" size="sm" onClick={onToggleCollapse} className={`w-full text-blue-200/70 hover:text-white hover:bg-white/10 ${collapsed ? 'justify-center' : 'justify-start'}`} data-testid="sidebar-collapse-btn">
            {collapsed ? <PanelLeft className="w-4 h-4" /> : <><PanelLeftClose className="w-4 h-4 mr-2" /><span className="text-xs">Collapse</span></>}
          </Button>
        </div>
      </aside>
    </>
  );
};

const Header = ({ onMenuClick }) => {
  const { user, organizations, currentOrg, switchOrganization, logout, isSuperAdmin } = useAuth();
  const role = user?.role;
  const { fiscalYears, selectedFY, switchFiscalYear, clearSelection, hasFiscalYears } = useFiscalYear();
  const { academicYears, selectedYear, switchYear, hasYears } = useAcademicYear();
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const isSchoolRoute = location.pathname.startsWith('/school');
  // Non-admin roles are pinned to the active academic year: if a stale/inactive year is
  // selected (e.g. left over in storage), snap them back to the active one.
  React.useEffect(() => {
    const canSwitchAllYears = ADMIN_ROLES.includes(role) || isSuperAdmin();
    if (canSwitchAllYears || !academicYears.length) return;
    if (selectedYear && !selectedYear.is_active) {
      const active = academicYears.find((y) => y.is_active);
      if (active && active.id !== selectedYear.id) switchYear(active);
    }
  }, [role, academicYears, selectedYear]); // eslint-disable-line

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-background/80 border-b border-border px-4 lg:px-6 py-3" data-testid="header">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 lg:gap-4">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={onMenuClick} data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></Button>

          {/* School identity */}
          <div className="hidden sm:flex items-center gap-2.5">
            <img src="/eli-crest.png" alt="E.L.I" className="w-8 h-8 object-contain" />
            <div className="leading-tight">
              <p className="text-sm font-semibold text-foreground">{t('app.school')}</p>
              <p className="text-[11px] text-muted-foreground">{t('app.shortName')}</p>
            </div>
          </div>

          {/* Org switcher - super admin only */}
          {user?.role === 'super_admin' && organizations.length >= 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3" data-testid="org-switcher">
                  <Building2 className="w-4 h-4 hidden sm:block" />
                  <span className="max-w-[100px] sm:max-w-[180px] truncate">{currentOrg?.name || 'Select Org'}</span>
                  <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="z-[60] w-[250px]">
                {organizations.map((org) => (
                  <DropdownMenuItem key={org.id} onClick={() => switchOrganization(org)} className={currentOrg?.id === org.id ? 'bg-primary/10' : ''} data-testid={`org-option-${org.id}`}>
                    <span className="text-sm">{org.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Fiscal Year selector (finance context) */}
          {hasFiscalYears && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className={`gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 hidden md:flex ${selectedFY?.status === 'closed' ? 'border-amber-500/50 text-amber-600' : 'border-emerald-500/50 text-emerald-600'}`} data-testid="fy-selector">
                  <Calendar className="w-4 h-4 hidden sm:block" />
                  <span className="max-w-[120px] truncate">{selectedFY ? selectedFY.name : 'All Periods'}</span>
                  {selectedFY?.status === 'closed' ? <Lock className="w-3 h-3" /> : selectedFY ? <Unlock className="w-3 h-3" /> : null}
                  <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="z-[60] w-[280px]">
                <DropdownMenuItem onClick={() => clearSelection()} className={!selectedFY ? 'bg-primary/10' : ''} data-testid="fy-option-all">
                  <span className="text-sm font-medium">All Periods</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {fiscalYears.map((fy) => (
                  <DropdownMenuItem key={fy.id} onClick={() => switchFiscalYear(fy)} className={selectedFY?.id === fy.id ? 'bg-primary/10' : ''} data-testid={`fy-option-${fy.id}`}>
                    <span className="text-sm">{fy.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Academic Year selector (school context) */}
          {isSchoolRoute && hasYears && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 hidden md:flex border-indigo-500/50 text-indigo-600" data-testid="ay-selector">
                  <GraduationCap className="w-4 h-4 hidden sm:block" />
                  <span className="max-w-[120px] truncate">{selectedYear ? selectedYear.name : t('academicYear.select', 'Academic Year')}</span>
                  <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="z-[60] w-[240px]">
                {academicYears.map((y) => {
                  // Only Super Admin / admin may switch to non-active years.
                  const canSwitchAllYears = ADMIN_ROLES.includes(role) || isSuperAdmin();
                  const locked = !canSwitchAllYears && !y.is_active;
                  return (
                    <DropdownMenuItem
                      key={y.id}
                      disabled={locked}
                      onSelect={(e) => { if (locked) { e.preventDefault(); return; } switchYear(y); }}
                      className={`${selectedYear?.id === y.id ? 'bg-primary/10' : ''} ${locked ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid={`ay-option-${y.id}`}
                    >
                      <span className="text-sm">{y.name}</span>
                      {y.is_active
                        ? <span className="ml-auto text-[10px] font-medium text-emerald-600">Active</span>
                        : (locked && <span className="ml-auto text-[10px] font-medium text-muted-foreground">Inactive</span>)}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="flex items-center gap-2 lg:gap-3">
          <LanguageSwitcher />
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-1 sm:gap-2 px-2 sm:px-3" data-testid="user-menu">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className="text-xs sm:text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{getRoleDisplayName(user.role)}</p>
                  </div>
                  <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="z-[60]">
                <DropdownMenuItem disabled><span className="text-muted-foreground text-sm">{user.email}</span></DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-500" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> {t('nav.signOut')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
};

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const navigate = useNavigate();

  const toggleCollapse = () => {
    setSidebarCollapsed((prev) => { const v = !prev; localStorage.setItem('sidebarCollapsed', String(v)); return v; });
  };

  useKeyboardShortcuts([
    { key: '?', shift: true, action: () => setShortcutsOpen(true) },
    { key: '/', action: () => { const el = document.querySelector('[data-testid*="search"] input, input[placeholder*="Search"], input[placeholder*="search"]'); if (el) el.focus(); } },
    { key: 'Escape', allowInInput: true, action: () => { const a = document.activeElement; if (a && a.tagName.toLowerCase() === 'input') a.blur(); }, preventDefault: false },
  ]);

  useSequenceShortcut((seq, reset) => {
    const routes = { gd: '/school', gs: '/school/students', gt: '/school/teachers', gc: '/school/classes' };
    if (routes[seq]) { navigate(routes[seq]); reset(); }
    if (seq.length >= 2 && !routes[seq]) reset();
  });

  const location = useLocation();
  const isPosPage = location.pathname === '/pos';

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} collapsed={sidebarCollapsed} onToggleCollapse={toggleCollapse} />
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <main className={`p-4 lg:p-6 animate-fade-in ${isPosPage ? 'pb-4' : 'pb-20'}`} data-testid="main-content">
          {children}
        </main>
      </div>
      {!isPosPage && <AppFooter />}
      <ShortcutsHelpDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </div>
  );
};

export default Layout;
